/**
 * What the editor's Run and Run tests buttons type into the terminal, and, on
 * the way back, which parts of what the terminal printed are places in the
 * learner's code.
 *
 * Typed, never run behind the learner's back. The command lands in the shell
 * they are already using, echoed like anything they typed themselves, so it
 * is in their history, its output is in their scrollback, and next time they
 * can type it without the button. The button teaches the command rather than
 * replacing it, which is the same bargain the rest of the product makes: real
 * bash, real node, graded on the state the learner leaves behind.
 *
 * Everything here is a pure function of paths, so it is tested without a
 * machine (web/test/run.test.ts). It knows only what the guest can run, node
 * and bash. A file nothing on the machine runs (SQL, JSON, Markdown) gets no
 * command rather than a guess.
 */

const JS = /\.[cm]?js$/;

/**
 * Whether `node --test` with no paths would run this file.
 *
 * Node's own default patterns, as of the Node 22 in the guest image:
 * `*.test.js`, `*-test.js`, `*_test.js`, `test-*.js`, `test.js`, and every
 * file under a directory named `test`, each in .js, .cjs and .mjs, with
 * `node_modules` skipped. Kept identical to Node's so that the button and the
 * command agree about what a test is — a narrower rule would hide Run tests
 * on a level where typing `node --test` works.
 */
export function isTestFile(path: string): boolean {
  if (!JS.test(path)) return false;
  const parts = path.split('/');
  if (parts.includes('node_modules')) return false;
  const stem = (parts[parts.length - 1] ?? '').replace(JS, '');
  if (stem === 'test' || stem.startsWith('test-')) return true;
  if (/[.\-_]test$/.test(stem)) return true;
  return parts.slice(0, -1).includes('test');
}

/**
 * A path as a person would type it: bare when bash reads it as one word,
 * single-quoted when it would not. A leading dash would read as an option,
 * so it gets `./` instead.
 */
function arg(path: string): string {
  if (path.startsWith('-')) return arg(`./${path}`);
  return /^[\w./@%+=:,-]+$/.test(path) ? path : `'${path.replace(/'/g, `'\\''`)}'`;
}

/**
 * The command that runs one file, relative to the level's root, or null when
 * nothing on the machine runs it.
 *
 * A test file gets `node --test <file>` rather than `node <file>`. Both run
 * it, but the first is what the level's own checks type and what the learner
 * will type at work.
 */
export function runFileCommand(path: string): string | null {
  if (JS.test(path)) return isTestFile(path) ? `node --test ${arg(path)}` : `node ${arg(path)}`;
  if (path.endsWith('.sh')) return `bash ${arg(path)}`;
  return null;
}

/** `node --test` when the workspace has anything it would run, else null. */
export function testCommand(entries: readonly { path: string; dir: boolean }[]): string | null {
  return entries.some((e) => !e.dir && isTestFile(e.path)) ? 'node --test' : null;
}

/**
 * The directory name the learner's prompt shows, or null when the line is not
 * a bare prompt.
 *
 * The guest's PS1 is `engineer@lattice:\W (branch) $ ` (sandbox/bashrc), and
 * `\W` is the last segment of the working directory. Reading it off the
 * screen is the whole reason this is instant: asking the guest instead is a
 * round trip on the command channel, measured at about three seconds under
 * emulation — three seconds of a Run button doing nothing visible.
 *
 * Anything else on the line — a half-typed command, a prompt the learner
 * redefined — is null, and null costs a `cd` (`commandLine`).
 */
export function promptDirectory(line: string): string | null {
  const m = /^engineer@lattice:(.+?)(?: \((?:HEAD detached at \S+|\S+)\))? \$\s*$/.exec(line);
  return m?.[1] ?? null;
}

/**
 * The line to type, given where the shell is standing.
 *
 * The commands above are relative to the level's root, because the file tree
 * is rooted there. A learner who has `cd`'d into `src/` would run them from
 * the wrong place and read "Cannot find module" as the button being broken,
 * so the line moves them back first — visibly, the way they would have had
 * to themselves.
 *
 * `here` is what the prompt shows (`promptDirectory`), which is a name and
 * not a path, so it is matched against the root's last segment. The one case
 * that reads wrong is a directory elsewhere with the root's own name, and
 * there the command fails in plain sight in the terminal. Every other doubt,
 * null included, gets the prefix, which is always right.
 */
export function commandLine(command: string, root: string, here: string | null): string {
  const name = root.split('/').filter(Boolean).at(-1) ?? '/';
  return here === name ? command : `cd ${arg(root)} && ${command}`;
}

/* --- Locations in what the terminal printed ------------------------------ */

/**
 * A `path:line` or `path:line:column` in a line of output: every frame of a
 * Node stack trace (`at inventoryReport (file:///workspace/storefront/src/
 * report.js:8:6)`), the location a failing test prints, a line of `grep -n`.
 * Clicking one opens the file at that line, which is most of what a debugger
 * is for when the question is "where did this come from".
 *
 * `start` and `end` are offsets into the text, `end` exclusive, and include a
 * `file://` in front, so the underline covers what the eye reads as the link.
 */
export interface Location {
  start: number;
  end: number;
  path: string;
  line: number;
  col: number | null;
}

// A path is at least two segments — `src/cart.js`, never a bare `cart.js` —
// which is what keeps times (`12:30:45`) and `host:port` out.
const LOCATION = /(?:file:\/\/)?((?:\.\/)?\/?[\w.@+-]+(?:\/[\w.@+-]+)+):(\d+)(?::(\d+))?/g;

export function findLocations(text: string): Location[] {
  const found: Location[] = [];
  for (const m of text.matchAll(LOCATION)) {
    const line = Number(m[2]);
    if (!(line >= 1)) continue;
    found.push({ start: m.index, end: m.index + m[0].length, path: m[1]!, line, col: m[3] ? Number(m[3]) : null });
  }
  return found;
}

/**
 * The file a location names, as a path in the editor's tree, or null when it
 * is not one of the learner's: Node's own frames (`node:internal/…`), a path
 * outside the level, a relative path that is not in the workspace.
 *
 * An absolute path is believed once it is under the root. Node prints frames
 * of files that exist, and the tree's listing is capped, so demanding a match
 * there would drop real files on a big repository. A relative path is
 * relative to wherever the shell was when it printed, which the screen does
 * not say, so it is believed only when the root has a file of that name.
 */
export function locate(path: string, root: string, files: ReadonlySet<string>): string | null {
  const prefix = root.endsWith('/') ? root : `${root}/`;
  if (path.startsWith('/')) return path.startsWith(prefix) && path.length > prefix.length ? path.slice(prefix.length) : null;
  const rel = path.replace(/^\.\//, '');
  return files.has(rel) ? rel : null;
}
